"use client";

import React from "react";
import { CircleAlert as AlertCircle, ArrowRight, Droplets, Flame, Gauge, PhoneCall, Pipette, ShieldAlert, Waves, Wrench } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
interface PlumbingIssueItem {
  id: string;
  title: string;
  description: string;
  urgency: string;
  symptoms: string;
  linkPath: string;
  linkText: string;
}
const plumbingIssues: PlumbingIssueItem[] = [{
  id: "clogged-drains",
  title: "Clogged Drains",
  description: "Slow-draining sinks, standing tub water, and persistent gurgling caused by deep line blockages and grease buildup.",
  urgency: "Moderate",
  symptoms: "Slow drainage, foul odors, standing water in basins",
  linkPath: "/drain-problems/",
  linkText: "Drain Problem Guide"
}, {
  id: "water-leaks",
  title: "Water Leaks",
  description: "Hidden slab leaks, dripping pipe joints, and wall dampness that drive up water bills and cause structural damage.",
  urgency: "High",
  symptoms: "Damp drywall, high water bill, musty smells",
  linkPath: "/water-leak-problems/",
  linkText: "Leak Diagnosis & Fixes"
}, {
  id: "no-hot-water",
  title: "No Hot Water",
  description: "Tank sediment buildup, faulty heating elements, or gas valve failures leaving your home without warm water.",
  urgency: "High",
  symptoms: "Lukewarm showers, popping tank noises, pilot outages",
  linkPath: "/water-heater-problems/",
  linkText: "Water Heater Solutions"
}, {
  id: "toilet-problems",
  title: "Toilet Problems",
  description: "Constantly running tanks, weak flushes, phantom fills, and frequent bowl backups in primary bathrooms.",
  urgency: "Moderate",
  symptoms: "Continuous running, weak flush, base seal leaks",
  linkPath: "/toilet-problems/",
  linkText: "Toilet Repair Info"
}, {
  id: "low-water-pressure",
  title: "Low Water Pressure",
  description: "Weak flow across fixtures caused by municipal line drops, mineral crusting, or hidden pipe restrictions.",
  urgency: "Moderate",
  symptoms: "Trickling showerheads, slow filling appliances",
  linkPath: "/water-pressure-problems/",
  linkText: "Water Pressure Guide"
}, {
  id: "sewer-backup",
  title: "Sewer Line Backup",
  description: "Multiple clogged fixtures, sewage bubbling up floor drains, and tree root intrusion into the main line.",
  urgency: "Emergency",
  symptoms: "Multiple backed up drains, sewer smell in yard",
  linkPath: "/sewer-line-repair/",
  linkText: "Sewer Line Repair"
}, {
  id: "burst-pipes",
  title: "Burst & Broken Pipes",
  description: "Sudden pipe ruptures, freezing weather cracking, or aged line splitting leading to rapid indoor flooding.",
  urgency: "Emergency",
  symptoms: "Gushing water, sudden pressure loss, wall noises",
  linkPath: "/pipe-problems/",
  linkText: "Pipe Repair Services"
}];
export default function StockbridgeIssues() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="stockbridgecommonplumbingissues" data-nav="light" className="bg-background text-foreground py-16 md:py-24">
      <div className="container mx-auto px-4 md:px-6 lg:px-8">
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-12 md:mb-16">
          <Badge variant="secondary" className="mb-4">
            Stockbridge, GA Plumbing Guide
          </Badge>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-default font-bold tracking-tight text-foreground mb-4">
            Common Plumbing Issues in Stockbridge
          </h2>
          <p className="text-muted-foreground text-base sm:text-lg font-default leading-relaxed">
            Local homes and businesses frequently face these specific plumbing challenges. 
            Identify your issue below to view symptoms, troubleshooting advice, and repair options.
          </p>
        </div>

        {/* Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {plumbingIssues.map((issue, index) => {
          const isEmergency = issue.urgency === "Emergency";
          return <motion.div key={issue.id} data-index={index} initial={shouldReduceMotion ? {
            opacity: 1,
            y: 0
          } : {
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: "-50px"
          }} transition={{
            duration: 0.4,
            delay: index * 0.05
          }} className="h-full flex flex-col">
                <Card className="h-full flex flex-col justify-between border border-border shadow-sm hover:shadow-md transition-[box-shadow,border-color] hover:border-primary/40 bg-card text-card-foreground">
                  <CardHeader>
                    <div className="flex items-center justify-between gap-3 mb-2">
                      <div className="size-10 rounded-lg bg-secondary flex items-center justify-center text-primary">
                        {index === 0 && <Droplets className="size-5" />}
                        {index === 1 && <Pipette className="size-5" />}
                        {index === 2 && <Flame className="size-5" />}
                        {index === 3 && <Wrench className="size-5" />}
                        {index === 4 && <Gauge className="size-5" />}
                        {index === 5 && <Waves className="size-5" />}
                        {index === 6 && <ShieldAlert className="size-5" />}
                      </div>
                      <Badge variant={isEmergency ? "destructive" : "secondary"} className="text-xs">
                        {issue.urgency}
                      </Badge>
                    </div>
                    <CardTitle className="text-xl font-bold font-default text-card-foreground">
                      {issue.title}
                    </CardTitle>
                    <CardDescription className="text-muted-foreground line-clamp-3">
                      {issue.description}
                    </CardDescription>
                  </CardHeader>

                  <CardContent className="space-y-3 pt-0">
                    <div className="rounded-md bg-muted/60 p-3 text-xs">
                      <span className="font-semibold text-foreground block mb-1">
                        Typical Indicators:
                      </span>
                      <span className="text-muted-foreground">
                        {issue.symptoms}
                      </span>
                    </div>
                  </CardContent>

                  <CardFooter className="pt-2">
                    <Button asChild variant="outline" className="w-full justify-between group">
                      <Link to={issue.linkPath}>
                        <span>{issue.linkText}</span>
                        <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
                      </Link>
                    </Button>
                  </CardFooter>
                </Card>
              </motion.div>;
        })}

          {/* Urgent Dispatch Card */}
          <motion.div data-index={plumbingIssues.length} initial={shouldReduceMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: "-50px"
        }} transition={{
          duration: 0.4,
          delay: plumbingIssues.length * 0.05
        }} className="h-full flex flex-col md:col-span-2 lg:col-span-1">
            <Card className="h-full flex flex-col justify-between border-2 border-primary bg-primary text-primary-foreground shadow-md">
              <CardHeader>
                <div className="flex items-center justify-between gap-3 mb-2">
                  <div className="size-10 rounded-lg bg-primary-foreground/10 flex items-center justify-center text-primary-foreground">
                    <AlertCircle className="size-5" />
                  </div>
                  <Badge variant="secondary" className="bg-accent text-accent-foreground">
                    24/7 Dispatch
                  </Badge>
                </div>
                <CardTitle className="text-xl font-bold font-default text-primary-foreground">
                  Facing an Unlisted Crisis?
                </CardTitle>
                <CardDescription className="text-primary-foreground/80">
                  Jonesboro Emergency Plumber dispatches fully equipped plumbing technicians across Stockbridge immediately.
                </CardDescription>
              </CardHeader>

              <CardContent className="space-y-3 pt-0">
                <div className="rounded-md bg-primary-foreground/10 p-3 text-xs text-primary-foreground/90">
                  <span className="font-semibold block mb-1">Immediate Response:</span>
                  Average local arrival under 60 minutes for active floods and sewer emergencies.
                </div>
              </CardContent>

              <CardFooter className="pt-2 flex flex-col sm:flex-row gap-2">
                <Button asChild variant="secondary" className="w-full bg-accent text-accent-foreground hover:bg-accent/90 justify-center">
                  <Link to="/emergency-plumbing/">
                    <PhoneCall className="size-4 mr-1.5" />
                    <span>Emergency Help</span>
                  </Link>
                </Button>
              </CardFooter>
            </Card>
          </motion.div>
        </div>

        {/* Bottom Helper Bar */}
        <div className="mt-12 p-6 rounded-xl bg-card border border-border flex flex-col sm:flex-row items-center justify-between gap-4 text-center sm:text-left">
          <div>
            <h3 className="font-default font-semibold text-card-foreground text-lg">
              Not sure which problem you have?
            </h3>
            <p className="text-sm text-muted-foreground">
              Review our complete problem directory or schedule an on-site diagnostic inspection.
            </p>
          </div>
          <Button asChild variant="default">
            <Link to="/problems/">
              <span>View All Problems</span>
              <ArrowRight className="size-4 ml-1.5" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}